import type { SupportedLanguage } from './types';

/**
 * Default structural terminology dictionary across all 7 Indian languages.
 * When society configuration has standard default terms, they resolve to the user's selected language.
 * When a society admin specifies a custom override (e.g. "Bungalow", "Villa Cluster"), that custom override is preserved.
 */
export const PROPERTY_TERMINOLOGY_MAP: Record<
  string,
  Record<SupportedLanguage, string>
> = {
  flat: {
    en: 'Flat',
    hi: 'फ्लैट',
    te: 'ఫ్లాట్',
    or: 'ଫ୍ଲାଟ୍',
    bn: 'ফ্ল্যাট',
    gu: 'ફ્લેટ',
    ur: 'فلیٹ',
  },
  flats: {
    en: 'Flats',
    hi: 'फ्लैट्स',
    te: 'ఫ్లాట్లు',
    or: 'ଫ୍ଲାଟ୍‌ଗୁଡ଼ିକ',
    bn: 'ফ্ল্যাটসমূহ',
    gu: 'ફ્લેટ્સ',
    ur: 'فلیٹس',
  },
  block: {
    en: 'Block',
    hi: 'ब्लॉक',
    te: 'బ్లాక్',
    or: 'ବ୍ଲକ୍',
    bn: 'ব্লক',
    gu: 'બ્લોક',
    ur: 'بلاک',
  },
  blocks: {
    en: 'Blocks',
    hi: 'ब्लॉक्स',
    te: 'బ్లాకులు',
    or: 'ବ୍ଲକ୍‌ଗୁଡ଼ିକ',
    bn: 'ব্লকসমূহ',
    gu: 'બ્લોક્સ',
    ur: 'بلاکس',
  },
  tower: {
    en: 'Tower',
    hi: 'टावर',
    te: 'టవర్',
    or: 'ଟାୱାର୍',
    bn: 'টাওয়ার',
    gu: 'ટાવર',
    ur: 'ٹاور',
  },
  towers: {
    en: 'Towers',
    hi: 'टावर्स',
    te: 'టవర్లు',
    or: 'ଟାୱାର୍‌ଗୁଡ଼ିକ',
    bn: 'টাওয়ারসমূহ',
    gu: 'ટાવર્સ',
    ur: 'ٹاورز',
  },
  house: {
    en: 'House',
    hi: 'मकान / घर',
    te: 'ఇల్లు / హౌస్',
    or: 'ଘର',
    bn: 'বাড়ি',
    gu: 'મકાન / ઘર',
    ur: 'مکان / گھر',
  },
  houses: {
    en: 'Houses',
    hi: 'मकान',
    te: 'ఇళ్ళు',
    or: 'ଘରଗୁଡ଼ିକ',
    bn: 'বাড়িগুলি',
    gu: 'ઘરો',
    ur: 'مکانات',
  },
  plot: {
    en: 'Plot',
    hi: 'प्लॉट',
    te: 'ప్లాట్',
    or: 'ପ୍ଲଟ୍',
    bn: 'প্লট',
    gu: 'પ્લોટ',
    ur: 'پلاٹ',
  },
  plots: {
    en: 'Plots',
    hi: 'प्लॉट्स',
    te: 'ప్లాట్లు',
    or: 'ପ୍ଲଟ୍‌ଗୁଡ଼ିକ',
    bn: 'প্লটসমূহ',
    gu: 'પ્લોટ્સ',
    ur: 'پلاٹس',
  },
  road: {
    en: 'Road / Street',
    hi: 'सड़क / मार्ग',
    te: 'రోడ్డు / వీధి',
    or: 'ରାସ୍ତା / ଗଳି',
    bn: 'রাস্তা / সরণি',
    gu: 'રસ્તો / શેરી',
    ur: 'سڑک / گلی',
  },
  villa: {
    en: 'Villa',
    hi: 'विला',
    te: 'విల్లా',
    or: 'ଭିଲ୍ଲା',
    bn: 'ভিলা',
    gu: 'વિલા',
    ur: 'ولا',
  },
  villas: {
    en: 'Villas',
    hi: 'विलास',
    te: 'విల్లాలు',
    or: 'ଭିଲ୍ଲାଗୁଡ଼ିକ',
    bn: 'ভিলাসমূহ',
    gu: 'વિલાસ',
    ur: 'ولا جات',
  },
  unit: {
    en: 'Unit',
    hi: 'इकाई (यूनिट)',
    te: 'యూనిట్',
    or: 'ୟୁନିଟ୍',
    bn: 'ইউনিট',
    gu: 'યુનિટ',
    ur: 'یونٹ',
  },
  units: {
    en: 'Units',
    hi: 'इकाइयां',
    te: 'యూనిట్లు',
    or: 'ୟୁନିଟ୍‌ଗୁଡ଼ିକ',
    bn: 'ইউনিটসমূহ',
    gu: 'યુનિટ્સ',
    ur: 'یونٹس',
  },
};

/**
 * Resolve a property term into the target language.
 * If term is a standard key, returns its translation.
 * Otherwise returns the custom configured label directly.
 */
export function resolveLocalizedPropertyTerm(
  term: string | undefined,
  lang: SupportedLanguage = 'en',
): string {
  if (!term) return '';
  const normalized = term.trim().toLowerCase();
  const entry = PROPERTY_TERMINOLOGY_MAP[normalized];
  if (entry && entry[lang]) {
    return entry[lang];
  }
  return term;
}

/**
 * Universal Database Status & Dynamic Enum localization map across all 7 supported languages.
 * Automatically translates database values (e.g. ACTIVE, TRIAL, EXPIRED, PAID, PENDING, RESOLVED, etc.)
 */
export const DATABASE_STATUS_MAP: Record<string, Record<SupportedLanguage, string>> = {
  ACTIVE: {
    en: 'Active',
    hi: 'सक्रिय',
    te: 'యాక్టివ్',
    or: 'ସକ୍ରିୟ',
    bn: 'সক্রিয়',
    gu: 'સક્રિય',
    ur: 'فعال',
  },
  TRIAL: {
    en: 'Trial',
    hi: 'ट्रायल',
    te: 'ట్రయల్',
    or: 'ପରୀକ୍ଷାମୂଳକ (ଟ୍ରାଏଲ୍)',
    bn: 'ট্রায়াল',
    gu: 'ટ્રાયલ',
    ur: 'آزمائشی',
  },
  FREE_TRIAL: {
    en: 'Free Trial',
    hi: 'निःशुल्क ट्रायल',
    te: 'ఉచిత ట్రయల్',
    or: 'ମାଗଣା ଟ୍ରାଏଲ୍',
    bn: 'ফ্রি ট্রায়াল',
    gu: 'મફત ટ્રાયલ',
    ur: 'مفت ٹرائل',
  },
  STANDARD: {
    en: 'Standard',
    hi: 'मानक (Standard)',
    te: 'స్టాండర్డ్',
    or: 'ଷ୍ଟାଣ୍ଡାର୍ଡ',
    bn: 'স্ট্যান্ডার্ড',
    gu: 'સ્ટાન્ડર્ડ',
    ur: 'معیاری',
  },
  GO: {
    en: 'Go',
    hi: 'गो (Go)',
    te: 'గో (Go)',
    or: 'ଗୋ (Go)',
    bn: 'গো (Go)',
    gu: 'ગો (Go)',
    ur: 'گو (Go)',
  },
  PLUS: {
    en: 'Plus',
    hi: 'प्लस (Plus)',
    te: 'ప్లస్ (Plus)',
    or: 'ପ୍ଲସ୍ (Plus)',
    bn: 'প্লাস (Plus)',
    gu: 'પ્લસ (Plus)',
    ur: 'پلس (Plus)',
  },
  PRO: {
    en: 'Pro',
    hi: 'प्रो (Pro)',
    te: 'ప్రో (Pro)',
    or: 'ପ୍ରୋ (Pro)',
    bn: 'প্রো (Pro)',
    gu: 'પ્રો (Pro)',
    ur: 'پرو (Pro)',
  },
  PREMIUM: {
    en: 'Premium',
    hi: 'प्रीमियम (Premium)',
    te: 'ప్రీమియం (Premium)',
    or: 'ପ୍ରିମିୟମ୍ (Premium)',
    bn: 'প্রিমিয়াম (Premium)',
    gu: 'પ્રીમિયમ (Premium)',
    ur: 'پریمیم (Premium)',
  },
  ENTERPRISE: {
    en: 'Enterprise',
    hi: 'एंटरप्राइज',
    te: 'ఎంటర్‌ప్రైజ్',
    or: 'ଏଣ୍ଟରପ୍ରାଇଜ୍',
    bn: 'এন্টারপ্রাইজ',
    gu: 'એન્ટરપ્રાઇઝ',
    ur: 'انٹرپرائز',
  },
  INACTIVE: {
    en: 'Inactive',
    hi: 'निष्क्रिय',
    te: 'ఇన్యాక్టివ్',
    or: 'ନିଷ୍କ୍ରିୟ',
    bn: 'নিষ্ক্রিয়',
    gu: 'નિષ્ક્રિય',
    ur: 'غیر فعال',
  },
  SUSPENDED: {
    en: 'Suspended',
    hi: 'निलंबित',
    te: 'తాత్కాలికంగా నిలిపివేయబడింది',
    or: 'ସ୍ଥଗିତ',
    bn: 'স্থগিত',
    gu: 'સ્થગિત',
    ur: 'معطل',
  },
  EXPIRED: {
    en: 'Expired',
    hi: 'समाप्त',
    te: 'గడువు ముగిసింది',
    or: 'ମିଆଦ ପୂର୍ଣ୍ଣ',
    bn: 'মেয়াদোত্তীর্ণ',
    gu: 'સમાપ્ત',
    ur: 'میعاد ختم',
  },
  PENDING: {
    en: 'Pending',
    hi: 'लंबित',
    te: 'పెండింగ్',
    or: 'ବିଚାରାଧୀନ',
    bn: 'মুলতুবি',
    gu: 'બાકી',
    ur: 'زیر التواء',
  },
  PAID: {
    en: 'Paid',
    hi: 'भुगतान किया गया',
    te: 'చెల్లించబడింది',
    or: 'ପୈଠ ହୋଇଛି',
    bn: 'পরিশোধিত',
    gu: 'ચુકવેલ',
    ur: 'ادا شدہ',
  },
  UNPAID: {
    en: 'Unpaid',
    hi: 'अदत्त (Unpaid)',
    te: 'చెల్లించలేదు',
    or: 'ଅପୈଠ',
    bn: 'অপরিশোধিত',
    gu: 'ન ચુકવેલ',
    ur: 'غیر ادا شدہ',
  },
  OVERDUE: {
    en: 'Overdue',
    hi: 'अतिदेय',
    te: 'గడువు దాటింది',
    or: 'ଅତିଦେୟ',
    bn: 'বকেয়া',
    gu: 'બાકી મુદત',
    ur: 'واجب الادا',
  },
  CANCELLED: {
    en: 'Cancelled',
    hi: 'रद्द',
    te: 'రద్దు చేయబడింది',
    or: 'ବାତିଲ୍',
    bn: 'বাতিল',
    gu: 'રદ કરેલ',
    ur: 'منسوخ',
  },
  RESOLVED: {
    en: 'Resolved',
    hi: 'समाधान किया गया',
    te: 'పరిష్కరించబడింది',
    or: 'ସମାଧାନ ହୋଇଛି',
    bn: 'সমাধান হয়েছে',
    gu: 'ઉકેલાયેલ',
    ur: 'حل شدہ',
  },
  OPEN: {
    en: 'Open',
    hi: 'खुला',
    te: 'తెరిచి ఉంది',
    or: 'ଖୋଲା',
    bn: 'উন্মুক্ত',
    gu: 'ખુલ્લું',
    ur: 'کھلا',
  },
  CLOSED: {
    en: 'Closed',
    hi: 'बंद',
    te: 'మూసివేయబడింది',
    or: 'ବନ୍ଦ',
    bn: 'বন্ধ',
    gu: 'બંધ',
    ur: 'بند',
  },
  INSIDE: {
    en: 'Inside',
    hi: 'परिसर के अंदर',
    te: 'లోపల ఉన్నారు',
    or: 'ପରିସର ଭିତରେ',
    bn: 'ভেতরে',
    gu: 'અંદર',
    ur: 'اندر',
  },
  CHECKED_OUT: {
    en: 'Checked Out',
    hi: 'बाहर गए (Checked Out)',
    te: 'బయటకు వెళ్లారు',
    or: 'ପ୍ରସ୍ଥାନ କରିଛନ୍ତି',
    bn: 'প্রস্থান করেছেন',
    gu: 'બહાર નીકળ્યા',
    ur: 'چیک آؤٹ',
  },
};

/**
 * Automatically resolves dynamic database enum values, plan names, or statuses into the active language.
 */
export function resolveLocalizedDbValue(
  value: string | undefined | null,
  lang: SupportedLanguage = 'en',
): string {
  if (!value) return '';
  const trimmed = value.trim();
  const upper = trimmed.toUpperCase();
  const normalizedKey = upper.replace(/\s+/g, '_');

  const entry = DATABASE_STATUS_MAP[normalizedKey] || DATABASE_STATUS_MAP[upper];
  if (entry && entry[lang]) {
    return entry[lang];
  }
  return trimmed;
}

